// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Flex,
    Dialog,
    DialogContent,
    DialogHeader,
    Heading,
    Text
} from '@locus-taxy/neo-ui';
import debounce from 'lodash/debounce';

import { APIErrorScreen } from 'common/components/core/errors';
import { ApiStatusEnum } from 'common/enums/API';
import { Input } from 'common/components/core/input';

import Skeleton from 'platform/settings/modules/orchestration/components/Skeleton';
import { NoRows } from 'platform/settings/modules/orchestration/components/DataTable/';

import useGetWaybillUnusedSearch from './useGetWaybillUnusedSearch';
import LoadMore from './LoadMore';

type Props = {
    carrierId: string,
    serviceType: string,
    translationBasePath: string,
    onDismiss: () => void
};

const WaybillDialog = ({
    carrierId,
    serviceType,
    translationBasePath,
    onDismiss
}: Props): React.Node => {
    const [searchKey, setSearchKey] = React.useState();

    const { t } = useTranslation('platform');

    const {
        status,
        data,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage
    } = useGetWaybillUnusedSearch({
        carrierId,
        serviceType,
        searchKey
    });

    const handleLoadMore = () => {
        if (hasNextPage) {
            fetchNextPage();
        }
    };

    const renderRows = () => {
        if (status === ApiStatusEnum.Loading) {
            return <Skeleton height={30} borderRadius={3} count={10} />;
        }

        if (status === ApiStatusEnum.Error) {
            return (
                <APIErrorScreen
                    actionLabel={t('retry')}
                    onActionClick={() => {}}
                    subtitle={t('genericDataFetchError')}
                />
            );
        }

        const [page] = data.pages;
        const { waybillNumbers } = page;

        if (waybillNumbers.size === 0) {
            return <NoRows />;
        }

        return (
            <>
                {data.pages?.map(page => (
                    <React.Fragment key={page}>
                        {page.waybillNumbers.map(waybillNumber => (
                            <Box
                                key={waybillNumber}
                                p={3}
                                backgroundColor="white"
                                borderRightStyle="solid"
                                borderRightColor="grey.200"
                                borderBottomStyle="solid"
                                borderBottomColor="grey.200"
                            >
                                <Text size="regular" color="grey.900">
                                    {waybillNumber}
                                </Text>
                            </Box>
                        ))}
                    </React.Fragment>
                ))}

                <LoadMore
                    isLoading={isFetchingNextPage}
                    onLoadMore={handleLoadMore}
                />
            </>
        );
    };

    const dialogTitle = t(`${translationBasePath}.waybillDialog.title`);
    const total = data?.pages[0].paginationInfo.total || 0;
    const count = total === 0 ? '' : `(${total})`;
    const title = `${dialogTitle} - ${carrierId} / ${serviceType} ${count}`;

    return (
        <Dialog isOpen sticky onDismiss={onDismiss} width="small">
            <DialogHeader>
                <Heading truncated title={title}>
                    {title}
                </Heading>
            </DialogHeader>

            <DialogContent background="grey">
                <Flex
                    flexDirection="column"
                    borderWidth="thin"
                    borderStyle="solid"
                    borderColor="grey.200"
                    backgroundColor="white"
                    // neo Dialog height is 80%, so taking 80vh here.
                    // 90px is the header height.
                    height="calc(80vh - 90px)"
                >
                    <Box
                        p={3}
                        backgroundColor="grey.100"
                        borderBottomStyle="solid"
                        borderBottomColor="grey.200"
                    >
                        <Heading size="regular" color="grey.600">
                            {t(
                                `${translationBasePath}.waybillDialog.headerName`
                            )}
                        </Heading>
                    </Box>

                    <Box
                        p={1}
                        backgroundColor="grey.100"
                        borderBottomStyle="solid"
                        borderBottomColor="grey.200"
                    >
                        <Input
                            type="text"
                            icon="search"
                            placeholder={t('translations:search')}
                            onChange={debounce(setSearchKey, 300)}
                        />
                    </Box>

                    <Box
                        height="100%"
                        overflow="auto"
                        sx={{
                            '&::-webkit-scrollbar': {
                                borderRadius: '20px',
                                height: '10px',
                                width: '10px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#b3b3b3',
                                border: '3px solid #fff',
                                borderRadius: '20px'
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                                borderRadius: '20px'
                            }
                        }}
                    >
                        {renderRows()}
                    </Box>
                </Flex>
            </DialogContent>
        </Dialog>
    );
};

export default WaybillDialog;
